import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Typography, Container, Grid, Card, CardContent } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PhoneIcon from '@mui/icons-material/Phone';
import AnimatedSection from '../components/AnimatedSection';
import "@fontsource/playfair-display";

const aboutBanner = '/assets/bridal_banner.jpeg';

const values = [
  {
    title: 'Excellence',
    desc: 'We deliver premium quality services with attention to every detail',
  },
  {
    title: 'Quality',
    desc: 'Your comfort and satisfaction are our top priorities',
  },
  {
    title: 'Artistry',
    desc: 'We blend artistry with expertise for stunning results',
  },
  {
    title: 'Luxury',
    desc: 'A premium experience in a sophisticated ambiance',
  },
];

const contactInfo = [
  {
    icon: <LocationOnIcon sx={{ fontSize: '2.5rem', color: '#FF0099', mb: 1 }} />,
    title: 'Location',
    desc: (
      <>
        Opp. Bombay Gaslight Stores
        <br />
        Asilmetta Junction
        <br />
        Visakhapatnam
      </>
    ),
  },
  {
    icon: <PhoneIcon sx={{ fontSize: '2.5rem', color: '#FF0099', mb: 1 }} />,
    title: 'Contact',
    desc: '+91 89776 26223',
  },
  {
    icon: <AccessTimeIcon sx={{ fontSize: '2.5rem', color: '#FF0099', mb: 1 }} />,
    title: 'Hours',
    desc: 'Mon - Sun: 9:00AM - 9:00PM',
  },
];

const testimonials = [
  {
    name: 'Priya Sharma',
    text: 'Absolutely love the bridal package! The team made me feel like a queen on my special day. Highly recommend Splash for any bride-to-be.',
  },
  {
    name: 'Anjali Reddy',
    text: 'Best salon in Vizag! The hair colour turned out exactly how I wanted. The stylists really listen and deliver amazing results every time.',
  },
  {
    name: 'Sneha Rao',
    text: 'The spa and facial treatments are so relaxing. My skin has never looked better. The ambience is luxurious and the staff is very professional.',
  },
  {
    name: 'Kavitha Devi',
    text: "I've been coming here for over a year now. The nail art is always on point and the hygiene standards are top-notch. Love this place!",
  },
  {
    name: 'Ramesh Kumar',
    text: "Great men's grooming experience. The beard styling and haircut were perfect. Finally a salon that caters well to men too!",
  },
  {
    name: 'Meena Patel',
    text: 'The keratin treatment transformed my hair completely. Smooth, shiny and so manageable now. Worth every penny spent here.',
  },
];

export default function AboutPage() {
  return (
    <Box>
      <Helmet>
        <title>About Splash Beauty Lounge | Top Unisex Salon in Vizag</title>
        <meta name="description" content="Learn about Splash Beauty Lounge, Vizag's trusted unisex salon with expert professionals offering premium beauty, hair and skincare services." />
        <meta name="keywords" content="about Splash Beauty Lounge, best salon Asilmetta, beauty experts Vizag, hair stylists Vizag, skincare specialists Vizag, salon professionals Vizag" />
      </Helmet>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          backgroundImage: `url(${aboutBanner})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          py: { xs: 8, md: 12 },
          textAlign: 'center',
          color: '#fff',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1, px: 3 }}>
          <Typography
            variant="h2"
            sx={{
              color: '#fff',
              fontFamily: '"Playfair Display", serif',
              fontSize: { xs: '2.5rem', md: '4rem' },
              mb: 2,
              textShadow: '0 2px 15px rgba(0,0,0,0.5)',
            }}
          >
            About Splash Beauty Lounge
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Lato", sans-serif',
              color: 'rgba(255,255,255,0.95)',
              fontSize: { xs: '1rem', md: '1.2rem' },
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Your destination for luxury beauty and wellness in Visakhapatnam
          </Typography>
        </Box>
      </Box>


      {/* Story Section */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <AnimatedSection>
          <Typography
            variant="h4"
            sx={{
              fontFamily: '"Playfair Display", serif',
              color: '#FF0099',
              textAlign: 'center',
              mb: 4,
              fontSize: { xs: '1.5rem', md: '2rem' },
            }}
          >
            Our Story
          </Typography>
          <Typography
            sx={{
              fontSize: '1rem',
              lineHeight: 1.8,
              color: '#555',
              mb: 3,
              textAlign: 'center',
            }}
          >
            Splash Beauty Lounge is Visakhapatnam's premier destination for luxury beauty and wellness. We have built our reputation on delivering exceptional services across a comprehensive range of beauty treatments - from hair styling and coloring to bridal makeup, skincare, nail care, and premium men's grooming.
          </Typography>
          <Typography
            sx={{
              fontSize: '1rem',
              lineHeight: 1.8,
              color: '#555',
              textAlign: 'center',
            }}
          >
            With expert stylists trained in the latest beauty trends, premium products from top international brands like L'Oreal and Schwarzkopf, and a commitment to luxury experience, we ensure every client leaves our salon feeling pampered and confident. We combine traditional beauty techniques with modern innovations to deliver personalized experiences that exceed expectations.
          </Typography>
        </AnimatedSection>
      </Container>

      {/* Core Values Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <AnimatedSection>
          <Typography
            variant="h4"
            sx={{
              fontFamily: '"Playfair Display", serif',
              color: '#FF0099',
              textAlign: 'center',
              mb: 6,
              fontSize: { xs: '1.5rem', md: '2rem' },
            }}
          >
            Our Values
          </Typography>
        </AnimatedSection>
        <Grid container spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
          {values.map((value, idx) => (
            <Grid item xs={12} sm={6} md={3} key={idx} sx={{ display: 'flex' }}>
              <AnimatedSection delay={idx * 0.1} sx={{ display: 'flex', width: '100%' }}>
                <Card
                  elevation={1}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    border: '1px solid #eee',
                    backgroundColor: '#fff',
                    textAlign: 'center',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 10px 30px rgba(255, 0, 153, 0.1)',
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, md: 2, lg: 4 }, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography
                      sx={{
                        fontFamily: '"Playfair Display", serif',
                        fontSize: '1.3rem',
                        fontWeight: 700,
                        color: '#FF0099',
                        mb: 2,
                      }}
                    >
                      {value.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.95rem',
                        color: '#666',
                        lineHeight: 1.6,
                      }}
                    >
                      {value.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </AnimatedSection>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Testimonials Section */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <AnimatedSection>
          <Typography
            variant="h4"
            sx={{
              fontFamily: '"Playfair Display", serif',
              color: '#FF0099',
              textAlign: 'center',
              mb: 6,
              fontSize: { xs: '1.5rem', md: '2rem' },
            }}
          >
            What Our Clients Say
          </Typography>
        </AnimatedSection>
        <Grid container spacing={3}>
          {testimonials.map((review, idx) => (
            <Grid item xs={12} md={6} key={idx}>
              <AnimatedSection delay={idx * 0.1}>
                <Card
                  elevation={1}
                  sx={{
                    background: '#fff',
                    border: '1px solid #eee',
                    borderRadius: 3,
                    p: 3,
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 10px 30px rgba(255, 0, 153, 0.1)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
                    {[...Array(5)].map((_, i) => (
                      <StarIcon key={i} sx={{ color: '#FFB800', fontSize: '1rem' }} />
                    ))}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.9rem',
                      color: '#555',
                      lineHeight: 1.7,
                      mb: 2,
                      fontStyle: 'italic',
                    }}
                  >
                    "{review.text}"
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: '#1a1a1a',
                      fontSize: '0.9rem',
                    }}
                  >
                    {review.name}
                  </Typography>
                </Card>
              </AnimatedSection>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Contact Information */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <AnimatedSection>
          <Typography
            variant="h4"
            sx={{
              fontFamily: '"Playfair Display", serif',
              color: '#FF0099',
              textAlign: 'center',
              mb: 6,
              fontSize: { xs: '1.5rem', md: '2rem' },
            }}
          >
            Visit Us Today
          </Typography>
        </AnimatedSection>
        <Grid container spacing={3} justifyContent="center">
          {contactInfo.map((info, idx) => (
            <Grid item xs={12} sm={6} md={4} lg={4} key={idx} sx={{ display: 'flex' }}>
              <AnimatedSection delay={idx * 0.1} sx={{ display: 'flex', width: '100%' }}>
                <Card
                  elevation={1}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    border: '1px solid #eee',
                    backgroundColor: '#fff',
                    textAlign: 'center',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 10px 30px rgba(255, 0, 153, 0.1)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    {info.icon}
                    <Typography
                      sx={{
                        fontFamily: '"Playfair Display", serif',
                        fontSize: '1.3rem',
                        fontWeight: 700,
                        color: '#1a1a1a',
                        mb: 2,
                      }}
                    >
                      {info.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.95rem',
                        color: '#666',
                        lineHeight: 1.6
                      }}
                    >
                      {info.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </AnimatedSection>
            </Grid>
          ))}
        </Grid>
      </Container>

    </Box>
  );
}
